/**
 * Nursery row slots: a place in a block is a row, a position along it, and —
 * in a block with a track through it — which side of the track.
 */

import type { RowSide } from '@/types/nursery';

/** What the "side of track" choice on the add-rows form sends. */
export type SideChoice = 'none' | 'left' | 'right' | 'both';

export const SIDES_FOR: Record<SideChoice, RowSide[]> = {
  none: [], left: ['left'], right: ['right'], both: ['left', 'right'],
};

const MAX_SEQUENCE = 100000;

/**
 * "10,20,30", a range, or a mix. A range between two tens ("10-60") is every
 * tenth — positions are numbered in tens so one can be slotted in between
 * later — and any other range ("1-5", "5-25") is every number. Anything that
 * is not a positive whole number is dropped; the form previews the result, so
 * a dropped token shows as a missing position rather than an error.
 */
export function parseSequences(input: string): number[] {
  const out = new Set<number>();
  for (const token of input.split(/[,\s]+/)) {
    const range = /^(\d+)-(\d+)$/.exec(token);
    if (range) {
      const from = Number(range[1]);
      const to = Math.min(Number(range[2]), MAX_SEQUENCE);
      const step = from % 10 === 0 && to % 10 === 0 ? 10 : 1;
      for (let n = from; n <= to; n += step) if (n > 0) out.add(n);
    } else if (/^\d+$/.test(token)) {
      const n = Number(token);
      if (n > 0 && n <= MAX_SEQUENCE) out.add(n);
    }
  }
  return [...out].sort((a, b) => a - b);
}

/** How many locations a run of rows makes: rows × sides × positions. */
export function countRowSlots(startRow: number, endRow: number, side: SideChoice, sequences: number): number {
  if (endRow < startRow) return 0;
  return (endRow - startRow + 1) * Math.max(SIDES_FOR[side].length, 1) * sequences;
}

/** "Row 5", or "Row 5 left" beside a track. */
export function rowLabel(row: number, side?: RowSide | null): string {
  return side ? `Row ${row} ${side}` : `Row ${row}`;
}

interface SlotPlace {
  block_name?: string | null;
  row_number?: number | null;
  row_side?: RowSide | null;
  sequence_number?: number | null;
  location_name?: string | null;
}

/** Where a lot stands, as a picker reads it: block · row (and side) · position. */
export function slotWhere(place: SlotPlace): string {
  return [
    place.block_name,
    // A place that is not a row slot (a shade house, a holding area) is named,
    // so a picker can find it with the row and position unknown.
    place.row_number != null ? rowLabel(place.row_number, place.row_side) : place.location_name,
    place.sequence_number != null ? `position ${place.sequence_number}` : null,
  ].filter(Boolean).join(' · ') || place.location_name || 'No location recorded';
}

interface SlotOrder extends SlotPlace {
  farm_name?: string | null;
}

const SIDE_ORDER: Record<string, number> = { left: 1, right: 2 };
const byName = (a?: string | null, b?: string | null) =>
  (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });
/** Numbers ascending; a place with none comes after every place with one. */
const byNumber = (a?: number | null, b?: number | null) =>
  Number(a == null) - Number(b == null) || (a ?? 0) - (b ?? 0);

/**
 * A farm's blocks: by the sequence set on the block, then by name with
 * numbers read as numbers ("Block 2" before "Block 10"). A block with no
 * sequence follows the ones that have one.
 */
export function compareBlocks(
  a: { name?: string | null; sort_order?: number | null },
  b: { name?: string | null; sort_order?: number | null },
): number {
  return byNumber(a.sort_order, b.sort_order) || byName(a.name, b.name);
}

/**
 * Walk order: farm, block, row, side of the track, position. Places that are
 * not row slots (a seed bed, a polyhouse) follow each block's rows, by name;
 * a lot with no place at all is last.
 */
export function compareSlots(a: SlotOrder, b: SlotOrder): number {
  const placed = (p: SlotOrder) => p.row_number != null || Boolean(p.location_name || p.block_name);
  return Number(!placed(a)) - Number(!placed(b))
    || byName(a.farm_name, b.farm_name)
    || byName(a.block_name, b.block_name)
    || byNumber(a.row_number, b.row_number)
    || (SIDE_ORDER[a.row_side ?? ''] ?? 0) - (SIDE_ORDER[b.row_side ?? ''] ?? 0)
    || byNumber(a.sequence_number, b.sequence_number)
    || byName(a.location_name, b.location_name);
}

interface StockItemNames {
  cultivar_name?: string;
  rootstock_name?: string | null;
  tree_type_name?: string;
  sku_code?: string | null;
}

/** "Cripps Pink / M9 · Bare root, 2-year": the tree, then how it is sold. */
export function stockItemLabel(item: StockItemNames): string {
  const tree = [item.cultivar_name, item.rootstock_name].filter(Boolean).join(' / ');
  return [tree || item.sku_code || 'Stock item', item.tree_type_name].filter(Boolean).join(' · ');
}
